import {t} from '@lingui/macro'
import useSWR from 'swr'

import type {CountryTransactionCountResponse, PostalAddressModel} from '@pleo-io/deimos'

import config from 'config'
import {useToaster} from '~/shell/toaster'
import {useCompanyUser} from '~/shell/user'

import {getDeimos, putDeimos} from './helpers'

import {request} from '../api'

const baseUrl = config.endpoints.api

export interface CompanyInfo {
    id: string | null
    currency: string | null
    velocity: string | null
}

export function useCompanyInfo() {
    const {companyId} = useCompanyUser()
    return useSWR<CompanyInfo, Error>(
        companyId ? `/rest/v1/companies/${companyId}/info` : null,
        getDeimos,
    )
}

export function useTransactionsByCountry() {
    const {companyId} = useCompanyUser()
    return useSWR<CountryTransactionCountResponse, Error>(
        companyId ? `/rest/v1/telesto/companies/${companyId}/country-transactions` : null,
        getDeimos,
    )
}

export function useBillingAdmins() {
    const {companyId} = useCompanyUser()

    const result = useSWR<any>(
        companyId ? `/rest/v1/companies/${companyId}/employees/?role=owner` : null,
        getDeimos,
    )

    return result
}

export function useBillingAdmin() {
    const {companyId} = useCompanyUser()
    const {showToast} = useToaster()

    const result = useSWR(companyId ? `rest/v1/billing/companies/${companyId}/billing-admin` : '')
    const updateBillingAdmin = async (billingAdminId: string) => {
        try {
            await putDeimos(
                `rest/v1/companies/${companyId}/billing-admin/?billingAdminId=${billingAdminId}`,
            ).then((response) => {
                if (response?.success) {
                    showToast(t`You have now set a new billing contact.`, {
                        level: 'success',
                        title: t`Success`,
                    })
                } else {
                    showToast(t`Was unable to set billing contact.`, {
                        level: 'error',
                        title: t`Error`,
                    })
                }
            })
        } catch (e) {
            showToast(t`Was unable to set billing contact.`, {
                level: 'error',
                title: t`Error`,
            })
        }
        result.mutate()
    }
    return {...result, mutations: {updateBillingAdmin}}
}

interface CreateCompanyProps {
    registryId?: string
    registrationNumber?: string
    incorporationDate?: string
    address?: PostalAddressModel
    name?: string
    numemployees?: string
}

interface CreateCompanyRequestOwner extends CreateCompanyProps {
    employeeId: string
}

interface CreateCompanyRequestPartner extends CreateCompanyProps {
    partnerId: string
}

type CreateCompanyRequest = CreateCompanyRequestOwner | CreateCompanyRequestPartner

export async function emailBalanceStatement(
    companyId: string,
    date: string,
    receipientEmail: string,
    receipientName: string,
) {
    return request(`${baseUrl}/rest/v1/companies/${companyId}/balance-statement`, {
        auth: 'user',
        method: 'GET',
        query: {
            date,
            check: false,
            receipientEmail,
            receipientName,
        },
    })
}

export async function enablePocket(companyId: string): Promise<{success: boolean}> {
    return request(`${baseUrl}/rest/v1/companies/${companyId}/pocket/enable`, {
        auth: 'user',
        method: 'PUT',
    })
}

export async function getBalanceDiff(companyId: string) {
    return request(`${baseUrl}/rest/v1/companies/${companyId}/balance`, {
        auth: 'user',
        method: 'GET',
    })
}

export async function downloadBalanceStatement(
    accessToken: string,
    companyId: string,
    formattedDate: string,
) {
    return request(
        `${baseUrl}/rest/v1/companies/${companyId}/balance-statement?date=${formattedDate}`,
        {bearer: accessToken, method: 'GET', responseType: 'arraybuffer'},
    )
}

export async function create(body: CreateCompanyRequest, forceManual?: boolean) {
    return request(`${baseUrl}/rest/v1/companies`, {
        auth: 'user',
        method: 'POST',
        body,
        query: forceManual ? {forceManual: true} : undefined,
    })
}

export async function createCompanyDocumentFile(
    companyId: string,
    documentId: string,
    file: File,
    onProgress?: (progress: number) => void,
) {
    return request(`${baseUrl}/rest/v1/companies/${companyId}/styx/documents/${documentId}/files`, {
        auth: 'user',
        method: 'POST',
        file,
        onProgress,
    })
}

export async function deleteStyxCompanyDocumentFile(
    companyId: string,
    documentId: string,
    fileId: string,
) {
    return request(
        `${baseUrl}/rest/v1/companies/${companyId}/styx/documents/${documentId}/files/${fileId}`,
        {
            auth: 'user',
            method: 'DELETE',
        },
    )
}

export async function performCompanyReview(companyId: string) {
    return request(`${baseUrl}/rest/v1/companies/${companyId}/styx/reviews`, {
        auth: 'user',
        method: 'POST',
    })
}

export async function createPersonDocumentFile(
    companyId: string,
    personId: string,
    documentId: string,
    file: File,
    onProgress?: (progress: number) => void,
) {
    return request(
        `${baseUrl}/rest/v1/companies/${companyId}/styx/persons/${personId}/documents/${documentId}/files`,
        {
            auth: 'user',
            method: 'POST',
            file,
            onProgress,
        },
    )
}

export async function deleteStyxPersonDocumentFile(
    companyId: string,
    personId: string,
    documentId: string,
    fileId: string,
) {
    return request(
        `${baseUrl}/rest/v1/companies/${companyId}/styx/persons/${personId}/documents/${documentId}/files/${fileId}`,
        {
            auth: 'user',
            method: 'DELETE',
        },
    )
}
